import { Period } from '@/types'

export const createDateString = (date: Date): string => {
  if (typeof date == 'string') {
    date = new Date(date)
  }
  const d = date.getDate()
  const m = date.getMonth() + 1
  const y = date.getFullYear()
  const h = date.getHours()
  const mn = date.getMinutes()
  const s = date.getSeconds()
  return (
    y +
    '-' +
    ('0' + m).slice(-2) +
    '-' +
    ('0' + d).slice(-2) +
    'T' +
    ('0' + h).slice(-2) +
    ':' +
    ('0' + mn).slice(-2) +
    ':' +
    ('0' + s).slice(-2)
  )
}

export function parseISOToDate(isoString: string): Date | undefined {
  const date = new Date(isoString)

  return isNaN(date.getTime()) ? undefined : date
}

export function parseDateFromString(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function formatISOToDate(isoString: string): string | undefined {
  // Checking that the string matches the ISO format (YYYY-MM-DDTHH:MM:SS)
  const isoRegex = /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}$/
  const match = isoString.match(isoRegex)

  if (!match) return undefined

  const [, year, month, day] = match

  return `${day}/${month}/${year}`
}

export const toUTCDate = (date: Date): Date => {
  // Get the components of date and time in UTC
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth() // Month from 0 to 11
  const day = date.getUTCDate()
  const hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const seconds = date.getUTCSeconds()
  const milliseconds = date.getUTCMilliseconds()
  // Create a new date in UTC
  return new Date(Date.UTC(year, month, day, hours, minutes, seconds, milliseconds))
}

export const createDateUTCString = (date: Date): string => {
  if (typeof date == 'string') {
    date = new Date(date)
  }
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth() // Month from 0 to 11
  const day = date.getUTCDate()
  const hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const seconds = date.getUTCSeconds()
  return (
    year +
    '-' +
    ('0' + month).slice(-2) +
    '-' +
    ('0' + day).slice(-2) +
    'T' +
    ('0' + hours).slice(-2) +
    ':' +
    ('0' + minutes).slice(-2) +
    ':' +
    ('0' + seconds).slice(-2) +
    'Z'
  )
}

export const getShortMonthNames = (t: (key: string) => string): string[] => [
  t('MetabolicLang.common.monthShort.Jan'),
  t('MetabolicLang.common.monthShort.Feb'),
  t('MetabolicLang.common.monthShort.Mar'),
  t('MetabolicLang.common.monthShort.Apr'),
  t('MetabolicLang.common.monthShort.May'),
  t('MetabolicLang.common.monthShort.Jun'),
  t('MetabolicLang.common.monthShort.Jul'),
  t('MetabolicLang.common.monthShort.Aug'),
  t('MetabolicLang.common.monthShort.Sep'),
  t('MetabolicLang.common.monthShort.Oct'),
  t('MetabolicLang.common.monthShort.Nov'),
  t('MetabolicLang.common.monthShort.Dec'),
]

export const getShortDayNames = (t: (key: string) => string): string[] => [
  t('MetabolicLang.common.dayShort.SU'),
  t('MetabolicLang.common.dayShort.MO'),
  t('MetabolicLang.common.dayShort.TU'),
  t('MetabolicLang.common.dayShort.WE'),
  t('MetabolicLang.common.dayShort.TH'),
  t('MetabolicLang.common.dayShort.FR'),
  t('MetabolicLang.common.dayShort.SA'),
]

export const calculatePeriod = (period: string): Period | null => {
  if (period === 'max') {
    return null
  } else if (period === '1month') {
    return new Period({
      dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)),
      dateTo: new Date(),
    })
  } else if (period === '6months') {
    return new Period({
      dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 6)),
      dateTo: new Date(),
    })
  } else if (period === '1year') {
    return new Period({
      dateFrom: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
      dateTo: new Date(),
    })
  }
  return new Period({
    dateFrom: new Date(),
    dateTo: new Date(),
  })
}

export function checkIfToday(date: string) {
  const inputDate = new Date(date)
  const today = new Date()
  return (
    inputDate.getFullYear() === today.getFullYear() &&
    inputDate.getMonth() === today.getMonth() &&
    inputDate.getDate() === today.getDate()
  )
}

export function getTimeString(time?: string | Date | null): string {
  if (!time) return '09:00'

  if (typeof time === 'string') {
    return time.substring(0, 5)
  }

  if (time instanceof Date) {
    const hours = time.getHours().toString().padStart(2, '0')
    const minutes = time.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  return '09:00'
}

// returns today's date in format '2025-01-01' in CET (according to "DateOnly.FromDateTime(DateTime.UtcNow)" on API)
export function getTodayDateOnly() {
  const date = new Date()
  const cetDateString = date.toLocaleString('en-US', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const [month, day, year] = cetDateString.split('/')
  return `${year}-${month}-${day}`
}

// returns yesterday's date in format '2025-01-01' in CET
export function getYesterdayDateOnly() {
  const now = new Date()
  const cetNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Paris' }))
  cetNow.setDate(cetNow.getDate() - 1)
  const cetDateString = cetNow.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const [month, day, year] = cetDateString.split('/')
  return `${year}-${month}-${day}`
}

export function toDateOnlyString(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

// formats an event date range in the viewer's local timezone:
// same calendar day -> "3 Aug, 14:00–16:00", otherwise "3 Aug, 14:00 – 4 Aug, 09:00"
export function formatAnnouncementDateRange(eventStartDate?: string, eventEndDate?: string): string {
  const dayFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
  const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })

  if (!eventStartDate && !eventEndDate) return ''

  if (!eventStartDate || !eventEndDate) {
    const single = new Date(eventStartDate || eventEndDate!)
    return `${dayFormatter.format(single)}, ${timeFormatter.format(single)}`
  }

  const start = new Date(eventStartDate)
  const end = new Date(eventEndDate)

  const sameDay = start.toDateString() === end.toDateString()

  if (sameDay) {
    return `${dayFormatter.format(start)}, ${timeFormatter.format(start)}–${timeFormatter.format(end)}`
  }

  return `${dayFormatter.format(start)}, ${timeFormatter.format(start)} – ${dayFormatter.format(end)}, ${timeFormatter.format(end)}`
}
